import React, {useState, useRef} from 'react';
import {Star} from 'lucide-react';
import cn from 'clsx';
import styles from './styles.module.css';

type Option = {
  label: string;
  value: number;
};

const getStarColor = (optionValue: number) => (value: number, hoverValue?: number) => {
  const currentValue = hoverValue !== undefined ? hoverValue : value;
  if (currentValue >= optionValue) return '#facc15';
  return '#e6e6e6';
};

const TOUCH_END_DELAY = 100;

export type Props = {
  value?: number;
  onChange?: (value: number) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  name?: string;
  className?: string;
  options: Option[];
};

export const RatingRadioGroup = ({
  value,
  onChange,
  label,
  name = 'rating',
  className,
  options,
}: Props) => {
  const [hoverValue, setHoverValue] = useState<number | undefined>(undefined);
  const isTouchDevice = useRef(false);

  const handleContainerMouseLeave = () => {
    setHoverValue(undefined);
  };

  const handleStarMouseEnter = (optionValue: number) => {
    if (!isTouchDevice.current) {
      setHoverValue(optionValue);
    }
  };

  const handleStarClick = (optionValue: number) => {
    onChange?.(optionValue);
  };
  const handleTouchStart = (optionValue: number) => {
    isTouchDevice.current = true;
    setHoverValue(optionValue);
  };

  const handleTouchEnd = () => {
    setTimeout(() => {
      setHoverValue(undefined);
    }, TOUCH_END_DELAY);
  };

  return (
    <fieldset className={cn(className, styles.RatingRadioGroup)}>
      {label && (
        <legend
          className={cn(styles.RatingRadioGroup__legend, 'RatingRadioGroup__legend')}
        >
          {label}
        </legend>
      )}
      <div
        className={cn(styles.RatingRadioGroup__list, 'RatingRadioGroup__list')}
        onMouseLeave={handleContainerMouseLeave}
      >
        {options.map(option => (
          <label
            key={option.value}
            className={styles.RatingRadioGroup__label}
            onMouseEnter={() => handleStarMouseEnter(option.value)}
            onTouchStart={() => handleTouchStart(option.value)}
            onTouchEnd={handleTouchEnd}
          >
            <input
              className={styles.RatingRadioGroup__input}
              type="radio"
              name={name}
              checked={value === option.value}
              value={option.value}
              onChange={() => handleStarClick(option.value)}
            />
            <Star
              className={styles.RatingRadioGroup__star}
              size={32}
              aria-label={option.label}
              color={getStarColor(option.value)(value || 0, hoverValue)}
              fill={getStarColor(option.value)(value || 0, hoverValue)}
            />
          </label>
        ))}
      </div>
    </fieldset>
  );
};
